import { Injectable } from '@angular/core';

/**
 * Handles the device / browser back button for in-page detail views on mobile.
 *
 * Many mobile screens render a "drill-down" view inside the same route (e.g.
 * the Social Media Performance page → "View Overall Followers"). Without
 * intercepting the device back button, pressing it would navigate the user
 * away from the current route entirely (back to the Dashboard) instead of
 * just closing the in-page detail view.
 *
 * This service works by pushing a virtual entry onto `window.history` when a
 * detail view is opened and listening for `popstate`. When the user presses
 * the device back button, the registered callback fires (closing the detail
 * view) instead of triggering an Angular route change.
 */
@Injectable({
  providedIn: 'root',
})
export class MobileBackNavigationService {
  private readonly STATE_KEY = '__mobileBackHandler';

  private readonly handlerStack: Array<{ id: string; callback: () => void }> = [];
  private readonly popStateListener = (event: PopStateEvent) =>
    this.handlePopState(event);
  private isListening = false;

  /**
   * Register a back handler for an in-page detail view.
   *
   * Pushes a virtual entry onto `history` so the next back-button press can be
   * intercepted, then runs `onBack` when that entry is popped. Each call must
   * be paired with either {@link pop} (in-page back button) or
   * {@link cleanup} (component destroyed without back press).
   */
  push(id: string, onBack: () => void): void {
    if (typeof window === 'undefined') return;

    window.history.pushState({ [this.STATE_KEY]: id }, '');
    this.handlerStack.push({ id, callback: onBack });

    if (!this.isListening) {
      window.addEventListener('popstate', this.popStateListener);
      this.isListening = true;
    }
  }

  /**
   * Programmatically dismiss the topmost detail view (e.g. user taps an
   * in-page back arrow). Triggers `history.back()` so the browser history
   * stays in sync and the registered callback runs through the same code
   * path as the device back button.
   */
  pop(id?: string): void {
    if (typeof window === 'undefined') return;
    if (this.handlerStack.length === 0) return;

    const top = this.handlerStack[this.handlerStack.length - 1];
    if (id && top.id !== id) {
      // Stale call — the handler has already been removed; nothing to do.
      return;
    }

    window.history.back();
  }

  /**
   * Remove a registered handler without manipulating browser history. Use
   * this from `ngOnDestroy` so a stale handler doesn't fire if the user
   * navigates away through some other means while a detail view is open.
   */
  cleanup(id: string): void {
    if (typeof window === 'undefined') return;

    const idx = this.handlerStack.findIndex((h) => h.id === id);
    if (idx !== -1) {
      this.handlerStack.splice(idx, 1);
    }

    if (this.handlerStack.length === 0 && this.isListening) {
      window.removeEventListener('popstate', this.popStateListener);
      this.isListening = false;
    }
  }

  /** Whether the given id currently has an active back handler. */
  isActive(id: string): boolean {
    return this.handlerStack.some((h) => h.id === id);
  }

  private handlePopState(_event: PopStateEvent): void {
    if (this.handlerStack.length === 0) return;

    const handler = this.handlerStack.pop()!;
    try {
      handler.callback();
    } catch (err) {
      console.error('[MobileBackNavigationService] back handler failed', err);
    }

    if (this.handlerStack.length === 0 && this.isListening) {
      window.removeEventListener('popstate', this.popStateListener);
      this.isListening = false;
    }
  }
}
